import { Component, Input, OnChanges } from '@angular/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { CultivationItemViewerComponent } from './cultivation-item-viewer/cultivation-item-viewer.component';

@Component({
  selector: 'vw-hierarchy-item',
  imports: [
    SharedModule,
    TranslateModule,
    NgClass,
    CultivationItemViewerComponent,
  ],
  templateUrl: './hierarchy-item.component.html',
  styleUrl: './hierarchy-item.component.scss'
})
export class HierarchyItemComponent implements OnChanges {
  @Input() hierarchy: any;
  /** Au moins une plante du groupe (toutes cultures et variétés confondues) attend une action manuelle. */
  actionRequired: boolean = false;
  /** Total de plants physiques du regroupement = Σ nbPlants sur toutes les cultures/variétés/plants. */
  totalPlants: number = 0;

  ngOnChanges() {
    this.actionRequired = this.computeActionRequired(this.hierarchy);
    this.totalPlants = this.computeTotalPlants(this.hierarchy);
  }

  /**
   * Somme des nbPlants de toutes les plantes du regroupement (= « nbPlants × quantité de plants »
   * de la culture). 0 si les plantes ne sont pas chargées (contexte calendrier) → on retombe alors
   * sur le décompte de variétés côté template.
   */
  private computeTotalPlants(hierarchy: any): number {
    let total = 0;
    for (const cultivation of hierarchy?.cultivations ?? []) {
      for (const variant of cultivation?.variants ?? []) {
        for (const plant of variant?.plants ?? []) {
          total += plant?.nbPlants ?? 1;
        }
      }
    }
    return total;
  }

  /**
   * Agrège l'état « action attendue » sur tout le regroupement : hierarchy → cultivations →
   * variants → plants. Important pour les cultures groupées (plusieurs cultivations dans une même
   * vignette) : un seul badge, mais qui reflète n'importe quelle plante du groupe en attente.
   */
  private computeActionRequired(hierarchy: any): boolean {
    for (const cultivation of hierarchy?.cultivations ?? []) {
      for (const variant of cultivation?.variants ?? []) {
        for (const plant of variant?.plants ?? []) {
          if (this.needsAction(plant)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /** Étape courante manuelle (isBlocking) dont la durée est écoulée (progression >= 1). */
  private needsAction(plant: any): boolean {
    return (
      !!plant?.currentGrowingStep?.isBlocking && this.stepProgression(plant) >= 1
    );
  }

  /**
   * Progression de l'étape courante recalculée côté client. myGarden n'expose pas le getter
   * currentGrowingStepProgression sur les plantes imbriquées : on reprend la même formule que le
   * back (jours écoulés / durée de l'étape, borné à [0, 1]).
   */
  private stepProgression(plant: any): number {
    const start = plant?.currentGrowingStepStartDate;
    const totalDays = plant?.currentGrowingStep?.durationDays;
    if (!start || !totalDays) {
      return 0;
    }
    const startMs = new Date(start).getTime();
    if (Number.isNaN(startMs)) {
      return 0;
    }
    const elapsedDays = (Date.now() - startMs) / (1000 * 60 * 60 * 24);
    const result = Math.min(1, Math.max(0, elapsedDays / totalDays));
    return Math.round((result + Number.EPSILON) * 100) / 100;
  }
}
